import { NO_FEATURE_ICON } from '@repo/lib';
import { z } from '../zod';

const PRODUCT_TYPE_ENUM = Object.freeze({
  CHARGE: 'charge',
  SUBSCRIPTION: 'subscription',
} as const);

export const ProductTypeEnumValidationSchema = z.enum(Object.values(PRODUCT_TYPE_ENUM)).meta({
  description: 'Product type. `charge` for one-time payments, `subscription` for recurring.',
  example: 'charge',
});

const PRODUCT_ROLE_ENUM = Object.freeze({
  PRODUCT: 'product',
  ORDER_BUMP: 'orderbump',
} as const);

export const ProductRoleEnumValidationSchema = z.enum(Object.values(PRODUCT_ROLE_ENUM)).meta({
  description:
    'What the product is for. `orderbump` products are the simplified add-ons a checkout page offers as order bumps and an event page offers as add-ons; they can never be sold as a page product, and a product cannot be converted between roles.',
  example: 'product',
});

const PRICING_TYPE_ENUM = Object.freeze({
  SINGLE: 'single',
  MULTIPLE: 'multiple',
} as const);

export const PricingTypeEnumValidationSchema = z.enum(Object.values(PRICING_TYPE_ENUM)).meta({
  description: 'Pricing type for this product - single price or multiple prices using variants.',
  example: 'single',
});

const TAX_BEHAVIOUR_ENUM = Object.freeze({
  INCLUSIVE: 'inclusive',
  EXCLUSIVE: 'exclusive',
  AUTOMATIC: 'automatic',
  DEFAULT: '',
});

export const TaxBehaviourEnumValidationSchema = z.enum(Object.values(TAX_BEHAVIOUR_ENUM)).meta({
  description:
    'Tax behavior for this product. Determines how taxes are calculated and displayed. Options include inclusive (tax included in price), exclusive (tax added on top of price), automatic (tax behavior determined by Stripe based on tax settings), or default (use default tax behavior).',
  example: 'exclusive',
});

const VARIANT_STATUS_ENUM = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  DELETED: 'deleted',
});

export const VariantStatusEnumValidationSchema = z.enum(Object.values(VARIANT_STATUS_ENUM)).meta({
  description:
    'Status of a product variant, indicating whether it is enabled, disabled, or deleted.',
  example: 'enabled',
});

const VARIANT_SELECTION_TYPE_ENUM = Object.freeze({
  SINGLE: 'single',
  MULTIPLE: 'multiple',
  QUANTITY: 'quantity',
});

export const VariantSelectionTypeEnumValidationSchema = z
  .enum(Object.values(VARIANT_SELECTION_TYPE_ENUM))
  .meta({
    description:
      'Selection type for product variants, indicating how customers can select variant options in a variant. Options include single (only one option can be selected), multiple (multiple options can be selected), or quantity (customer can specify quantity for each option).',
    example: 'single',
  });

// The icon a price badge can lead with. Keys are Heroicons mini names in
// kebab-case; packages/checkout/src/lib/badgeIconKeys.js carries the same list
// for the two apps that draw them (asserted equal by the API's parity test), so
// a new icon lands in both places or in neither.
export const BADGE_ICON_KEYS = [
  'star',
  'sparkles',
  'fire',
  'bolt',
  'rocket',
  'trophy',
  'check',
  'x-mark',
  'plus',
  'arrow-right',
  'check-badge',
  'check-circle',
  'shield-check',
  'thumbs-up',
  'heart',
  'smile',
  'light-bulb',
  'sun',
  'moon',
  'gift',
  'tag',
  'percent',
  'dollar',
  'banknotes',
  'credit-card',
  'shopping-bag',
  'shopping-cart',
  'truck',
  'clock',
  'calendar',
  'bell',
  'megaphone',
  'trending-up',
  'chart',
  'refresh',
  'lock',
  'key',
  'ticket',
  'users',
  'academic-cap',
  'book',
  'music',
  'video',
  'document',
  'download',
  'mobile',
  'camera',
  'paint-brush',
  'puzzle',
  'cube',
  'globe',
  'map-pin',
  'storefront',
  'tools',
  'beaker',
  'lifebuoy',
  'chat',
  'envelope',
  'phone',
  'cake',
  'scale',
  'eye',
] as const;

export type BadgeIconKey = (typeof BADGE_ICON_KEYS)[number];

export const BadgeIconEnumValidationSchema = z.enum(BADGE_ICON_KEYS).meta({
  description: 'Icon shown before the badge text.',
  example: 'star',
});

// A feature line can also carry no icon at all; a badge or call out cannot,
// since without an icon the text simply stands alone there.
export const FEATURE_ICON_KEYS = [...BADGE_ICON_KEYS, NO_FEATURE_ICON] as const;

export const FeatureIconEnumValidationSchema = z.enum(FEATURE_ICON_KEYS).meta({
  description: 'Icon leading a feature line, or `none` for no icon.',
  example: 'check-circle',
});
